import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ScreenHeader, Surface, Button, IconButton, Badge, Divider, Icon, FeedbackMsg, inputCss, AlunoSearch, FormField } from '../components/ui';

const POR_PAGINA = 10;

const API_URL = process.env.REACT_APP_API_URL;

const MATERIAS = ['Língua Portuguesa', 'Língua Estrangeira', 'Matemática', 'Biologia', 'Física', 'Química', 'História', 'Geografia', 'Outros'];

const MATERIA_TONES = {
  'Língua Portuguesa': 'accent', 'Língua Estrangeira': 'accent',
  'Matemática': 'success', 'Biologia': 'success',
  'Física': 'warn', 'Química': 'danger',
  'História': 'warn', 'Geografia': 'neutral', 'Outros': 'neutral',
};

function fileTypeLabel(url) {
  if (!url) return 'Arq';
  const ext = (url || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return 'PDF';
  if (['mp4', 'webm', 'mov', 'avi'].includes(ext)) return 'Vid';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'Img';
  if (['doc', 'docx'].includes(ext)) return 'Doc';
  if (['xls', 'xlsx'].includes(ext)) return 'Xls';
  return 'Arq';
}

const fileIconColors = { PDF: '#e03', Vid: '#8b5cf6', Img: '#0891b2', Doc: '#2563eb', Xls: '#16a34a', Arq: 'var(--text-tertiary)' };

function UploadModal({ alunos, onClose, onSaved }) {
  const [form, setForm] = useState({ titulo: '', materia: '', descricao: '', aluno_id: '' });
  const [arquivo, setArquivo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const selectStyle = { height: 40, padding: '0 12px', fontSize: 14, width: '100%', background: 'var(--bg-sunken)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', outline: 'none' };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.titulo || !form.materia || !arquivo) { setMsg('Preencha todos os campos obrigatórios.'); return; }
    if (arquivo.size > 30 * 1024 * 1024) { setMsg('Arquivo excede o limite de 30MB.'); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append('titulo', form.titulo);
    fd.append('arquivo', arquivo);
    fd.append('materia', form.materia);
    if (form.descricao) fd.append('descricao', form.descricao);
    if (form.aluno_id && form.aluno_id !== 'todos') fd.append('aluno_id', form.aluno_id);
    try {
      await axios.post(`${API_URL}/conteudos`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
      onSaved(); onClose();
    } catch (err) {
      setMsg(err.response?.data?.erro || 'Erro ao enviar.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mema-scroll" style={{ width: '100%', maxWidth: 480, background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0', boxShadow: 'var(--shadow-lg)', animation: 'fadeSlideUp .25s cubic-bezier(.22,1,.36,1)', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--gap-3)', borderBottom: '1px solid var(--border-hairline)', position: 'sticky', top: 0, background: 'var(--bg-surface)', zIndex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Novo conteúdo</div>
          <IconButton icon="close" onClick={onClose} variant="ghost"/>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 'var(--gap-3)', display: 'flex', flexDirection: 'column', gap: 'var(--gap-2)' }}>
          {[
            { name: 'titulo', label: 'Título', type: 'text', required: true },
          ].map(f => (
            <div key={f.name} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>{f.label}</label>
              <input name={f.name} type={f.type} value={form[f.name]} required={f.required}
                onChange={e => setForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={inputCss}/>
            </div>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Matéria</label>
            <select value={form.materia} onChange={e => setForm(p => ({ ...p, materia: e.target.value }))} style={selectStyle} required>
              <option value="">Selecione</option>
              {MATERIAS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <FormField label="Disponível para">
            <AlunoSearch
              alunos={alunos}
              value={form.aluno_id}
              onChange={id => setForm(p => ({ ...p, aluno_id: id }))}
              placeholder="Todos os alunos (buscar para restringir)"
            />
          </FormField>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Descrição (opcional)</label>
            <textarea value={form.descricao} onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))} rows={2}
              style={{ ...inputCss, height: 'auto', padding: '10px 12px', resize: 'vertical' }}/>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Arquivo (máx. 30MB)</label>
            <input type="file" onChange={e => setArquivo(e.target.files[0])} required
              style={{ fontSize: 13, color: 'var(--text-primary)', padding: '8px 0' }}/>
          </div>
          {msg && <FeedbackMsg msg={msg}/>}
          <Button variant="primary" size="lg" disabled={saving} style={{ width: '100%', marginTop: 4 }}>
            {saving ? 'Enviando…' : 'Enviar conteúdo'}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function Conteudos() {
  const { tipo, usuarioId } = useAuth();
  const [conteudos, setConteudos] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [filtroMateria, setFiltroMateria] = useState('Todos');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [msg, setMsg] = useState('');

  function carregar() {
    axios.get(`${API_URL}/conteudos`, { withCredentials: true })
      .then(r => setConteudos(r.data)).catch(() => {});
  }

  useEffect(() => {
    carregar();
    if (tipo === 'professora') {
      axios.get(`${API_URL}/alunos_aprovados`, { withCredentials: true })
        .then(r => setAlunos(r.data)).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtrados = conteudos.filter(c => {
    if (filtroMateria !== 'Todos' && c.materia !== filtroMateria) return false;
    if (busca && !c.titulo.toLowerCase().includes(busca.toLowerCase())) return false;
    if (tipo === 'professora') return true;
    return !c.aluno_id || String(c.aluno_id) === String(usuarioId);
  });

  const totalPaginas = Math.ceil(filtrados.length / POR_PAGINA);
  const paginados = filtrados.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  function excluir(id) {
    if (!window.confirm('Excluir este conteúdo?')) return;
    axios.delete(`${API_URL}/conteudos/${id}`, { withCredentials: true })
      .then(() => { carregar(); setMsg('Conteúdo excluído.'); setTimeout(() => setMsg(''), 3000); })
      .catch(() => setMsg('Erro ao excluir.'));
  }

  return (
    <div className="mema-screen" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)', paddingBottom: 90 }}>
      <ScreenHeader title="Conteúdos" action={
        tipo === 'professora'
          ? <IconButton icon="plus" variant="accent" size={36} onClick={() => setModalOpen(true)} label="Novo conteúdo"/>
          : null
      }/>

      {/* Search */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-md)' }}>
          <Icon name="search" size={15} style={{ color: 'var(--text-tertiary)' }}/>
          <input type="text" placeholder="Buscar conteúdo…" value={busca} onChange={e => { setBusca(e.target.value); setPagina(1); }}
            style={{ flex: 1, border: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}/>
        </div>
      </div>

      {/* Subject filter chips */}
      <div style={{ padding: '0 var(--gap-3)', display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }} className="mema-scroll">
        {['Todos', ...MATERIAS].map(m => (
          <button key={m} onClick={() => { setFiltroMateria(m); setPagina(1); }} style={{
            height: 28, padding: '0 12px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
            fontFamily: 'inherit', fontSize: 12, fontWeight: 540, transition: 'all .12s',
            background: filtroMateria === m ? 'var(--text-primary)' : 'var(--bg-surface)',
            color: filtroMateria === m ? 'var(--text-inverse)' : 'var(--text-secondary)',
            boxShadow: filtroMateria === m ? 'none' : 'var(--shadow-sm)',
            border: '1px solid ' + (filtroMateria === m ? 'transparent' : 'var(--border-hairline)'),
          }}>{m}</button>
        ))}
      </div>

      <div style={{ padding: '0 var(--gap-3)' }}>
        {msg && <div style={{ marginBottom: 'var(--gap-2)' }}><FeedbackMsg msg={msg}/></div>}

        {filtrados.length === 0 ? (
          <Surface padded style={{ textAlign: 'center', padding: 'var(--gap-4)' }}>
            <Icon name="book" size={28} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.35, color: 'var(--text-tertiary)' }}/>
            <div style={{ fontSize: 14, fontWeight: 540, color: 'var(--text-primary)' }}>Nenhum conteúdo disponível</div>
          </Surface>
        ) : (
          <>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 'var(--gap-2)', textAlign: 'right' }}>
              {filtrados.length} conteúdo{filtrados.length !== 1 ? 's' : ''} · página {pagina}/{totalPaginas}
            </div>
            <Surface>
              {paginados.map((c, i) => {
                const label = fileTypeLabel(c.nome_arquivo);
                return (
                  <React.Fragment key={c.id}>
                    {i > 0 && <Divider/>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-2)', padding: 'var(--pad-card)' }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 'var(--radius-md)', flexShrink: 0,
                        background: 'var(--bg-sunken)', color: fileIconColors[label] || 'var(--text-tertiary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3,
                      }}>{label}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 540, color: 'var(--text-primary)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                          {c.titulo}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                          <Badge tone={MATERIA_TONES[c.materia] || 'neutral'}>{c.materia}</Badge>
                          {c.para_aluno_nome && <span style={{ fontSize: 11, color: 'var(--danger)' }}>· {c.para_aluno_nome}</span>}
                          {!c.aluno_id && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>· para todos</span>}
                        </div>
                        {c.descricao && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{c.descricao}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <a href={API_URL + c.url} target="_blank" rel="noopener noreferrer" download>
                          <IconButton icon="download" size={32} variant="surface"/>
                        </a>
                        {tipo === 'professora' && (
                          <IconButton icon="close" size={32} variant="ghost" style={{ color: 'var(--danger)' }} onClick={() => excluir(c.id)} label="Excluir"/>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </Surface>

            {totalPaginas > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--gap-2)', marginTop: 'var(--gap-2)' }}>
                <IconButton icon="chevron-left" size={32} variant="surface" disabled={pagina === 1} onClick={() => setPagina(p => p - 1)} label="Anterior"/>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)', minWidth: 70, textAlign: 'center' }}>
                  {pagina} / {totalPaginas}
                </span>
                <IconButton icon="chevron-right" size={32} variant="surface" disabled={pagina === totalPaginas} onClick={() => setPagina(p => p + 1)} label="Próxima"/>
              </div>
            )}
          </>
        )}
      </div>

      {modalOpen && (
        <UploadModal alunos={alunos} onClose={() => setModalOpen(false)} onSaved={() => { carregar(); setMsg('Conteúdo enviado!'); setTimeout(() => setMsg(''), 3000); }}/>
      )}
    </div>
  );
}
